import React, { useEffect, useState } from "react";
import { motion, useSpring, useTransform } from "framer-motion";
import { Lightbulb as LightbulbIcon } from "lucide-react";

/* A tiny isometric box that follows the entered dimensions. Decoration with a job:
   it makes wrong numbers (a 100 × 2 × 2 “box”) look wrong at a glance. */
export function MiniBox({ l, b, h }) {
  const max = Math.max(l, b, h, 1);
  const L = 18 + (l / max) * 26;
  const B = 9 + (b / max) * 14;
  const H = 12 + (h / max) * 24;
  const x0 = 7, y0 = 14 + B;
  const top = `${x0},${y0} ${x0 + B},${y0 - B} ${x0 + B + L},${y0 - B} ${x0 + L},${y0}`;
  const front = `${x0},${y0} ${x0 + L},${y0} ${x0 + L},${y0 + H} ${x0},${y0 + H}`;
  const side = `${x0 + L},${y0} ${x0 + B + L},${y0 - B} ${x0 + B + L},${y0 - B + H} ${x0 + L},${y0 + H}`;
  return (
    <svg width="74" height="62" viewBox="0 0 74 62" aria-hidden="true" className="flex-shrink-0">
      <polygon points={top} fill="#FFFFFF" stroke="#94A3B8" strokeWidth="1.5" strokeLinejoin="round" />
      <polygon points={front} fill="#FFFFFF" stroke="#94A3B8" strokeWidth="1.5" strokeLinejoin="round" />
      <polygon points={side} fill="#FFFFFF" stroke="#94A3B8" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
}

/* A number that springs to its value and ticks along the way. */
export function AnimatedNumber({ value, decimals = 0, className }) {
  const mv = useSpring(0, { stiffness: 90, damping: 18 });
  const [display, setDisplay] = useState((Number(value) || 0).toFixed(decimals));
  useEffect(() => { mv.set(Number(value) || 0); }, [value, mv]);
  useEffect(() => {
    const unsub = mv.on("change", (v) => setDisplay((Number(v) || 0).toFixed(decimals)));
    return () => unsub();
  }, [mv, decimals]);
  return <span className={className}>{display}</span>;
}

/* The Package step's hero: an isometric box that springs to the live L×B×H, a dial that
   sweeps to the dead weight, and the charged-weight read-out — the wow centrepiece. */
export default function PackageStudio({ lCm, bCm, hCm, l, b, h, unitL, unitB, unitH, weightDisp, unitW, deadKg, volKg, billedBasis, billedKg }) {
  const lS = useSpring(0, { stiffness: 130, damping: 20 });
  const bS = useSpring(0, { stiffness: 130, damping: 20 });
  const hS = useSpring(0, { stiffness: 130, damping: 20 });
  useEffect(() => { lS.set(Number(lCm) || 0); }, [lCm, lS]);
  useEffect(() => { bS.set(Number(bCm) || 0); }, [bCm, bS]);
  useEffect(() => { hS.set(Number(hCm) || 0); }, [hCm, hS]);

  // Isometric geometry, anchored with generous margins so the dimension callouts sit OUTSIDE the box.
  const G = useTransform([lS, bS, hS], ([lc, bc, hc]) => {
    const max = Math.max(lc, bc, hc, 1);
    const L = 60 + (lc / max) * 130;
    const B = 24 + (bc / max) * 44;
    const H = 44 + (hc / max) * 118;
    return { L, B, H, x0: 100, y0: 96 };
  });
  const topPts = useTransform(G, ({ L, B, x0, y0 }) => `${x0},${y0} ${x0 + B},${y0 - B} ${x0 + B + L},${y0 - B} ${x0 + L},${y0}`);
  const frontPts = useTransform(G, ({ L, H, x0, y0 }) => `${x0},${y0} ${x0 + L},${y0} ${x0 + L},${y0 + H} ${x0},${y0 + H}`);
  const sidePts = useTransform(G, ({ L, B, H, x0, y0 }) => `${x0 + L},${y0} ${x0 + B + L},${y0 - B} ${x0 + B + L},${y0 - B + H} ${x0 + L},${y0 + H}`);
  const logoX = useTransform(G, ({ L, H, x0 }) => x0 + L / 2 - (Math.min(L, H) * 0.7) / 2);
  const logoY = useTransform(G, ({ L, H, y0 }) => y0 + H / 2 - (Math.min(L, H) * 0.7 / 1.25) / 2);
  const logoW = useTransform(G, ({ L, H }) => Math.min(L, H) * 0.7);
  const logoH = useTransform(G, ({ L, H }) => Math.min(L, H) * 0.7 / 1.25);

  // L — dimension line below the box (extension lines + end serifs + centred label).
  const Lx1 = useTransform(G, g => g.x0);
  const Lx2 = useTransform(G, g => g.x0 + g.L);
  const Ly = useTransform(G, g => g.y0 + g.H + 20);
  const Ley = useTransform(G, g => g.y0 + g.H + 5);
  const LyT = useTransform(Ly, v => v - 4);
  const LyB = useTransform(Ly, v => v + 4);
  const Lmx = useTransform(G, g => g.x0 + g.L / 2);
  const Lly = useTransform(G, g => g.y0 + g.H + 37);
  // H — dimension line to the left.
  const Hx = useTransform(G, g => g.x0 - 20);
  const Hy1 = useTransform(G, g => g.y0);
  const Hy2 = useTransform(G, g => g.y0 + g.H);
  const Hex = useTransform(G, g => g.x0 - 5);
  const HxL = useTransform(Hx, v => v - 4);
  const HxR = useTransform(Hx, v => v + 4);
  const Hmy = useTransform(G, g => g.y0 + g.H / 2);
  const Hlx = useTransform(G, g => g.x0 - 28);
  // B — depth: a real dimension line on the top-LEFT depth edge, offset up-left (the one direction
  // that is always clear of the box in this isometric view). Extension lines + serifs, like L and H.
  const BO = 13;
  const Bx1 = useTransform(G, g => g.x0 - BO);
  const By1 = useTransform(G, g => g.y0 - BO);
  const Bx2 = useTransform(G, g => g.x0 + g.B - BO);
  const By2 = useTransform(G, g => g.y0 - g.B - BO);
  const Bex0 = useTransform(G, g => g.x0);
  const Bey0 = useTransform(G, g => g.y0);
  const Bex1 = useTransform(G, g => g.x0 + g.B);
  const Bey1 = useTransform(G, g => g.y0 - g.B);
  const Bx1a = useTransform(Bx1, v => v - 3); const By1a = useTransform(By1, v => v - 3);
  const Bx1b = useTransform(Bx1, v => v + 3); const By1b = useTransform(By1, v => v + 3);
  const Bx2a = useTransform(Bx2, v => v - 3); const By2a = useTransform(By2, v => v - 3);
  const Bx2b = useTransform(Bx2, v => v + 3); const By2b = useTransform(By2, v => v + 3);
  const Blx = useTransform(G, g => g.x0 + g.B / 2 - BO - 6);
  const Bly = useTransform(G, g => g.y0 - g.B / 2 - BO - 6);

  // Weight dial — cube-root scale so even a 10 g change moves the needle.
  // Auto-ranging scale: pick the smallest "nice" full-scale that keeps the weight comfortably
  // on the dial (with headroom), so the needle is never pegged/overflowed and always reads a
  // meaningful position — from 10 g to hundreds of kg.
  const wKg = Math.max(Number(deadKg) || 0, 0);
  const NICE_MAX = [1, 2, 5, 10, 25, 50, 100, 250, 500, 1000, 2500, 5000];
  const dynMax = NICE_MAX.find(m => m >= wKg * 1.3) || NICE_MAX[NICE_MAX.length - 1];
  const frac = Math.min(Math.max(Math.cbrt(wKg / dynMax), 0), 1);
  const angle = useSpring(-122, { stiffness: 90, damping: 15 });
  useEffect(() => { angle.set(-122 + frac * 244); }, [frac, angle]);
  const CX = 92, CY = 94, R = 58;
  const nx = useTransform(angle, (a) => CX + Math.sin((a * Math.PI) / 180) * (R - 8));
  const ny = useTransform(angle, (a) => CY - Math.cos((a * Math.PI) / 180) * (R - 8));
  const polar = (d, r) => [CX + Math.sin((d * Math.PI) / 180) * r, CY - Math.cos((d * Math.PI) / 180) * r];
  const arc = (fromDeg, toDeg) => {
    const [x1, y1] = polar(fromDeg, R); const [x2, y2] = polar(toDeg, R);
    return `M ${x1} ${y1} A ${R} ${R} 0 ${Math.abs(toDeg - fromDeg) > 180 ? 1 : 0} 1 ${x2} ${y2}`;
  };
  const ticks = Array.from({ length: 9 }, (_, i) => -122 + (i / 8) * 244);
  const chargedKg = billedKg != null ? billedKg : (Number(deadKg) || 0);
  const DIM = "#3877FC";

  return (
    <div className="mt-4 space-y-4">
      <div className="grid grid-cols-1 gap-4 rounded-2xl border border-[#E4E9F1] bg-gradient-to-b from-[#FAFBFF] to-[#F3F6FC] p-5 md:grid-cols-[minmax(0,1.2fr)_auto_minmax(0,1fr)] md:items-center">
        {/* Cardboard box with brand + technical dimension callouts */}
        <svg viewBox="0 0 360 300" className="h-[268px] w-full" aria-hidden="true">
          <defs>
            <linearGradient id="boxTop" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#F0DDB4" /><stop offset="1" stopColor="#E2C793" /></linearGradient>
            <linearGradient id="boxFront" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#E4C893" /><stop offset="1" stopColor="#D2AE72" /></linearGradient>
            <linearGradient id="boxSide" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stopColor="#C6A268" /><stop offset="1" stopColor="#B08A50" /></linearGradient>
          </defs>
          <motion.polygon points={sidePts} fill="url(#boxSide)" stroke="#8F7038" strokeWidth="1.5" strokeLinejoin="round" />
          <motion.polygon points={topPts} fill="url(#boxTop)" stroke="#8F7038" strokeWidth="1.5" strokeLinejoin="round" />
          <motion.polygon points={frontPts} fill="url(#boxFront)" stroke="#8F7038" strokeWidth="1.5" strokeLinejoin="round" />
          <motion.image href="/logo.png" x={logoX} y={logoY} width={logoW} height={logoH} preserveAspectRatio="xMidYMid meet" opacity="0.9" />

          {/* L */}
          <motion.line x1={Lx1} y1={Ley} x2={Lx1} y2={Ly} stroke={DIM} strokeWidth="1.3" opacity="0.55" />
          <motion.line x1={Lx2} y1={Ley} x2={Lx2} y2={Ly} stroke={DIM} strokeWidth="1.3" opacity="0.55" />
          <motion.line x1={Lx1} y1={Ly} x2={Lx2} y2={Ly} stroke={DIM} strokeWidth="2" />
          <motion.line x1={Lx1} y1={LyT} x2={Lx1} y2={LyB} stroke={DIM} strokeWidth="2" />
          <motion.line x1={Lx2} y1={LyT} x2={Lx2} y2={LyB} stroke={DIM} strokeWidth="2" />
          <motion.text x={Lmx} y={Lly} textAnchor="middle" className="fill-slate-800" style={{ fontSize: 12, fontWeight: 800 }}>{`L · ${l || 0} ${unitL}`}</motion.text>

          {/* H */}
          <motion.line x1={Hex} y1={Hy1} x2={Hx} y2={Hy1} stroke={DIM} strokeWidth="1.3" opacity="0.55" />
          <motion.line x1={Hex} y1={Hy2} x2={Hx} y2={Hy2} stroke={DIM} strokeWidth="1.3" opacity="0.55" />
          <motion.line x1={Hx} y1={Hy1} x2={Hx} y2={Hy2} stroke={DIM} strokeWidth="2" />
          <motion.line x1={HxL} y1={Hy1} x2={HxR} y2={Hy1} stroke={DIM} strokeWidth="2" />
          <motion.line x1={HxL} y1={Hy2} x2={HxR} y2={Hy2} stroke={DIM} strokeWidth="2" />
          <motion.text x={Hlx} y={Hmy} textAnchor="end" dominantBaseline="middle" className="fill-slate-800" style={{ fontSize: 12, fontWeight: 800 }}>{`H · ${h || 0} ${unitH}`}</motion.text>

          {/* B — depth dimension line (top-left depth edge, offset up-left) */}
          <motion.line x1={Bex0} y1={Bey0} x2={Bx1} y2={By1} stroke={DIM} strokeWidth="1.3" opacity="0.55" />
          <motion.line x1={Bex1} y1={Bey1} x2={Bx2} y2={By2} stroke={DIM} strokeWidth="1.3" opacity="0.55" />
          <motion.line x1={Bx1} y1={By1} x2={Bx2} y2={By2} stroke={DIM} strokeWidth="2" />
          <motion.line x1={Bx1a} y1={By1a} x2={Bx1b} y2={By1b} stroke={DIM} strokeWidth="2" />
          <motion.line x1={Bx2a} y1={By2a} x2={Bx2b} y2={By2b} stroke={DIM} strokeWidth="2" />
          <motion.text x={Blx} y={Bly} textAnchor="end" dominantBaseline="middle" className="fill-slate-800" style={{ fontSize: 12, fontWeight: 800 }}>{`B · ${b || 0} ${unitB}`}</motion.text>
        </svg>

        {/* Weight dial */}
        <div className="flex flex-col items-center justify-center">
          <svg viewBox="0 0 184 126" className="h-[116px] w-full max-w-[184px]" aria-hidden="true">
            <path d={arc(-122, 122)} fill="none" stroke="#E2EAF8" strokeWidth="9" strokeLinecap="round" />
            {frac > 0 && <path d={arc(-122, -122 + frac * 244)} fill="none" stroke="#3877FC" strokeWidth="9" strokeLinecap="round" />}
            {ticks.map((t, i) => {
              const [x1, y1] = polar(t, R - 14); const [x2, y2] = polar(t, R - 9);
              return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#AFC0DE" strokeWidth="1.5" strokeLinecap="round" />;
            })}
            <motion.line x1={CX} y1={CY} x2={nx} y2={ny} stroke="#1E3A8A" strokeWidth="2.5" strokeLinecap="round" />
            <circle cx={CX} cy={CY} r="5.5" fill="#1E3A8A" />
            <circle cx={CX} cy={CY} r="2" fill="#fff" />
          </svg>
          <div className="-mt-2 text-center">
            <div className="flex items-baseline justify-center gap-1">
              <AnimatedNumber value={weightDisp} decimals={unitW === "kg" ? 3 : 0} className="text-[22px] font-bold tabular-nums text-slate-900" />
              <span className="text-[13px] font-semibold text-slate-500">{unitW}</span>
            </div>
            <div className="text-[10.5px] font-semibold uppercase tracking-wide text-slate-400">Dead weight</div>
            <div className="mt-0.5 text-[9.5px] font-medium tabular-nums text-slate-400">scale 0–{dynMax} kg</div>
          </div>
        </div>

        {/* Charged weight */}
        <div className="rounded-xl bg-white/80 px-4 py-3.5 ring-1 ring-inset ring-[#E7ECF5]">
          <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Chargeable weight</div>
          <div className="mt-0.5 flex items-baseline gap-1.5">
            <AnimatedNumber value={chargedKg} decimals={2} className="text-[28px] font-extrabold leading-none tracking-tight tabular-nums text-[#2A59BD]" />
            <span className="text-[15px] font-bold text-[#2A59BD]">kg</span>
          </div>
          <div className="mt-2.5 flex flex-wrap items-baseline gap-x-4 gap-y-1 border-t border-slate-100 pt-2.5 text-[12.5px] font-medium text-slate-500">
            <span>Dead <b className={`tabular-nums ${billedBasis === "dead" ? "text-[#2A59BD]" : "text-slate-700"}`}>{(deadKg || 0).toFixed(2)}</b> kg</span>
            {volKg != null && <span>Volumetric <b className={`tabular-nums ${billedBasis === "vol" ? "text-[#2A59BD]" : "text-slate-700"}`}>{volKg.toFixed(2)}</b> kg</span>}
            {volKg == null && <span className="text-slate-400">Add L×B×H for volumetric</span>}
          </div>
          <p className="mt-1.5 text-[11px] font-medium leading-relaxed text-slate-400">Billed on the higher of dead vs volumetric — (L × B × H) / 5000.</p>
        </div>
      </div>

      {/* Tip */}
      <div className="flex items-start gap-3 rounded-xl border border-amber-200/70 bg-amber-50/70 px-4 py-3">
        <span className="mt-0.5 grid h-6 w-6 flex-shrink-0 place-items-center rounded-lg bg-amber-100 text-amber-600"><LightbulbIcon className="h-3.5 w-3.5" /></span>
        <p className="text-[12.5px] font-medium leading-relaxed text-amber-800">Enter the <b>actual packed weight</b> and the <b>true L × B × H of the shipment box</b>. If the courier re-measures a bigger or heavier box after pickup, it raises a weight dispute and you're charged the difference.</p>
      </div>
    </div>
  );
}
